import { formatDashboardUnitQuantity } from "@/features/dashboard/metricFormatters";
import type { DashboardUnitQuantity } from "@/features/dashboard/types";
import "./DashboardLedger.css";

const QUANTITY_CSS = `
  .dashboard-ledger-quantity {
    container-name: dashboard-quantity;
    container-type: inline-size;
    min-width: 0;
    border-block: 1px solid var(--ledger-border);
    background: var(--ledger-surface);
  }
  .dashboard-ledger-quantity__grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    min-width: 0;
  }
  .dashboard-ledger-quantity__item {
    width: 100%;
    min-width: 0;
    min-height: 64px;
    padding: 12px 15px;
    border-right: 1px solid var(--ledger-border);
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
  }
  button.dashboard-ledger-quantity__item { cursor: pointer; }
  button.dashboard-ledger-quantity__item:hover { background: var(--ledger-surface-muted); }
  button.dashboard-ledger-quantity__item:focus-visible {
    position: relative;
    z-index: 1;
    outline: 2px solid var(--ledger-accent);
    outline-offset: -2px;
  }
  .dashboard-ledger-quantity__item:last-child { border-right: 0; }
  .dashboard-ledger-quantity__label {
    display: block;
    color: var(--ledger-muted);
    font-size: 0.75rem;
    font-weight: 700;
  }
  .dashboard-ledger-quantity__value {
    display: block;
    margin-top: 4px;
    font-family: "Barlow Condensed", "Arial Narrow", sans-serif;
    font-size: clamp(1.15rem, 2.2vw, 1.55rem);
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
  }
  .dashboard-ledger-quantity__value[data-length="long"] {
    font-size: clamp(0.95rem, 1.8vw, 1.25rem);
  }
  .dashboard-ledger-quantity__value[data-length="xlong"] {
    font-size: clamp(0.78rem, 1.45vw, 1rem);
    letter-spacing: -0.02em;
  }
  .dashboard-ledger-quantity__item[data-zero="true"] {
    color: var(--ledger-muted);
  }
  .dashboard-ledger-quantity__item[data-zero="true"] .dashboard-ledger-quantity__value {
    font-weight: 600;
    opacity: 0.72;
  }
  @container dashboard-quantity (max-width: 40rem) {
    .dashboard-ledger-quantity__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .dashboard-ledger-quantity__item:nth-child(2n) { border-right: 0; }
    .dashboard-ledger-quantity__item:nth-child(n + 3) { border-top: 1px solid var(--ledger-border); }
    .dashboard-ledger-quantity__item:last-child { grid-column: span 2; border-right: 0; }
  }
  @media (max-width: 640px) {
    .dashboard-ledger-quantity__value {
      font-size: clamp(1.05rem, 5vw, 1.4rem);
    }
    .dashboard-ledger-quantity__value[data-length="long"] {
      font-size: clamp(0.86rem, 4vw, 1.08rem);
    }
  }
  @container dashboard-quantity (max-width: 20rem) {
    .dashboard-ledger-quantity__grid { grid-template-columns: minmax(0, 1fr); }
    .dashboard-ledger-quantity__item { border-right: 0; }
    .dashboard-ledger-quantity__item:last-child { grid-column: auto; }
    .dashboard-ledger-quantity__item:nth-child(n + 2) {
      border-top: 1px solid var(--ledger-border);
    }
    .dashboard-ledger-quantity__value[data-length="xlong"] {
      font-size: 0.875rem;
    }
  }
`;

export interface DashboardQuantityBreakdownProps {
  quantities: readonly DashboardUnitQuantity[];
  ariaLabel?: string;
  onSelect?: (unit: (typeof units)[number]["unit"]) => void;
}

const units = [
  { unit: "kg", label: "Quilos" },
  { unit: "cx", label: "Caixas" },
  { unit: "bdj", label: "Bandejas" },
  { unit: "sc", label: "Sacos" },
  { unit: "un", label: "Unidades" },
] as const;

export function DashboardQuantityBreakdown({
  quantities,
  ariaLabel = "Quantidades",
  onSelect,
}: DashboardQuantityBreakdownProps) {
  const byUnit = new Map(
    quantities.map((quantity) => [quantity.unit, quantity]),
  );

  return (
    <>
      <style>{QUANTITY_CSS}</style>
      <section
        className="dashboard-ledger dashboard-ledger-quantity"
        aria-label={ariaLabel}
      >
        <div className="dashboard-ledger-quantity__grid">
          {units.map(({ unit, label }) => {
            const raw = byUnit.get(unit)?.quantity ?? "0";
            const formatted = formatDashboardUnitQuantity(unit, raw);
            const value = formatted?.full ?? "—";
            const renderedValue = `${value} ${unit}`;
            const zero = value === "0";
            const content = (
              <>
                <span className="dashboard-ledger-quantity__label">
                  {label}
                </span>
                <strong
                  className="dashboard-ledger-quantity__value dashboard-ledger-number"
                  data-length={
                    renderedValue.length > 15
                      ? "xlong"
                      : renderedValue.length > 11
                        ? "long"
                        : "regular"
                  }
                >
                  {renderedValue}
                </strong>
              </>
            );
            const shared = {
              className: "dashboard-ledger-quantity__item",
              "data-testid": `dashboard-quantity-${unit}`,
              "data-zero": zero ? "true" : "false",
              "aria-label": `${label}: ${renderedValue}`,
            } as const;
            return onSelect ? (
              <button
                {...shared}
                type="button"
                key={unit}
                onClick={() => onSelect(unit)}
              >
                {content}
              </button>
            ) : (
              <div {...shared} key={unit}>
                {content}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
